import { action, makeObservable, observable, override, runInAction } from 'mobx'
import {
  convertFieldValue,
  convertFieldValueFromServer,
  IFormField,
  IFormFieldMode,
} from '@migrate/utils/form/form'
import UserNotificationStore, { UserNotificationType } from '@migrate/stores/UserNotificationStore'
import { getEntityTypeStore, stores } from '@migrate/stores'
import i18n from 'i18next'
import { HyperMediaStore } from '@migrate/stores/HyperMediaStore'
import EditAssumptionStore from '@migrate/components/navigator/stores/EditAssumptionStore'
import {
  AssumptionValue,
  Category,
  EntityDetail,
  EntityStorageType,
} from '@portal/pkg-api/gen/navigator'
import { AssumptionApi } from '@portal/pkg-api/navigator'
import { IngestionApi } from '@portal/pkg-api/gen/ingestion'
import { data } from '@portal/pkg-api/shared'
import FieldAssumptionProcessor from '@migrate/utils/form/FieldAssumptionProcessor'
import ViewPickerStore from '@migrate/components/navigator/stores/ViewPickerStore'
import { RestApiConfiguration } from '@portal/pkg-api/config'

export interface ICategory {
  id: number
  name: string
  description?: string
  maxNumberOfColumns: number
  fields: IFormField[]
  hidden: boolean
  open: boolean
}

export default class EntityDetailTabStore extends EditAssumptionStore {
  public static STORE_NAME = 'entityDetailTabStore'

  @observable public categories: ICategory[]
  @observable public noAssumptions: boolean
  @observable public detailsLoaded: boolean
  @observable public error: boolean
  @observable public errorMessage: string
  @observable public dialogOpen: boolean
  @observable public dialogType: string
  @observable public dialogField?: IFormField
  @observable public filteredAssumptions?: number[]
  @observable public searchTerm?: string
  @observable public categoriesAllOpen: boolean

  hyperMediaStore: HyperMediaStore
  categoryValues: Category[]
  entityId: number
  entityDetail?: EntityDetail
  showActions: boolean = true
  viewPickerStore: ViewPickerStore | undefined
  eventSource: EventSource | undefined
  private sseErrorCount = 0
  private static readonly MAX_SSE_ERRORS = 5

  private ingestionApi: IngestionApi

  constructor(hyperMediaStore: HyperMediaStore) {
    super()
    this.hyperMediaStore = hyperMediaStore
    this.categoryValues = []
    this.categories = []
    this.entityId = -1
    this.noAssumptions = false
    this.editField = null

    this.error = false
    this.detailsLoaded = false
    this.errorMessage = ''
    this.dialogOpen = false
    this.dialogType = ''
    this.dialogField = undefined
    this.categoriesAllOpen = false

    this.ingestionApi = new IngestionApi(RestApiConfiguration)

    makeObservable(this)
  }

  async pollBulk() {
    await this.pullBulk()
    this.sseErrorCount = 0
    this.eventSource = new EventSource(
      `/api/rest/v1/ingestion/bulk/entities/${this.entityId}/infoList/sse`,
      {
        withCredentials: true,
      },
    )

    this.eventSource.onmessage = (event) => {
      this.sseErrorCount = 0
      const data = JSON.parse(event.data)
      this.onMessageReceived(data)
      if (data.terminal) {
        this.eventSource?.close()
      }
    }

    this.eventSource.onerror = (_event) => {
      this.sseErrorCount++
      if (this.sseErrorCount >= EntityDetailTabStore.MAX_SSE_ERRORS) {
        this.eventSource?.close()
      }
      // Otherwise let EventSource auto-reconnect; the backend sends current
      // Redis state on each new subscription so late reconnects still work.
    }
  }

  async pullBulk() {
    const response = await data(this.ingestionApi.getBulkInfoList(this.entityId))
    if (response) {
      this.onMessageReceived(response)
    }
  }

  @action
  async reload(entityId?: number, entityTypeId?: number, entityDetail?: EntityDetail) {
    this.detailsLoaded = false
    this.assumptionFields = []
    this.assumptionFieldMap = {}
    this.categories = []
    await this.setSelectedEntity(
      entityId ?? this.entityId,
      entityTypeId ?? this.entityType!.id,
      entityDetail ?? this.entityDetail!,
    )
  }
  @action
  async setSelectedEntity(entityId: number, entityTypeId: number, entityDetail: EntityDetail) {
    if (this.eventSource) {
      this.eventSource.close()
    }
    this.sseErrorCount = 0
    this.entityId = entityId
    this.setEntityType(getEntityTypeStore().findById(entityTypeId))
    this.entityDetail = entityDetail
    this.showActions = EntityStorageType.TABULAR !== this.entityType!.storageType

    await Promise.all([
      this.loadAssumptionValues(this.entityId),
      this.loadConditionalAssumptions(entityTypeId),
    ])

    this.processConditionalSelects()
    this.processConditionalHiddens()
    this.fieldAssumptionProcessor = new FieldAssumptionProcessor(this.assumptionFields)
    this.fieldAssumptionProcessor.updateConditionals()

    // do this after processing conditional hiddens to remove categories of all hidden fields
    this.processFilteredAssumptions()

    // expand the first category by default
    if (this.categories && this.categories[0]) {
      this.setCategoryState(this.categories[0], true)
    }

    runInAction(() => {
      this.detailsLoaded = true
    })
  }

  @action
  setFilterAssumptions(assumptions?: number[]) {
    this.filteredAssumptions = assumptions
  }

  @action
  search(query: string) {
    this.searchTerm = query
    this.processFilteredAssumptions()
  }

  @action
  setDisconnected(field: IFormField, disconnected: boolean) {
    field.meta.model.disconnected = disconnected
  }

  @override
  async handlePostEdit(field: IFormField) {
    const model: AssumptionValue = field.meta.model

    const assumptionValue: AssumptionValue = {
      id: model.id ? model.id : undefined,
      entityId: Number(this.entityId),
      assumptionId: model.assumption!.id,
      value: convertFieldValue(field),
      value2: field.meta.value2,
    }

    let updatedValue: AssumptionValue
    try {
      updatedValue = !assumptionValue.id
        ? await data(
            AssumptionApi.createAssumptionValue(assumptionValue, { mercatusNotifyOnError: false }),
          )
        : await data(
            AssumptionApi.saveAssumptionValue(assumptionValue.id, assumptionValue, {
              mercatusNotifyOnError: false,
            }),
          )
    } catch (e: any) {
      const errorText =
        typeof e.response.data === 'string'
          ? i18n.t(e.response.data)
          : i18n.t('navigator.detail.errorSaving')
      const notificationStore: UserNotificationStore = stores[UserNotificationStore.STORE_NAME]
      if (notificationStore) {
        notificationStore.addNotification(UserNotificationType.ERROR, errorText)
      }
      console.error(errorText, e)
    }

    runInAction(() => {
      if (updatedValue) {
        model.id = updatedValue.id!
        field.id = updatedValue.assumptionId!
        field.value = convertFieldValueFromServer(field, updatedValue.value)
      }
    })
  }

  @override
  afterUpdateConditionals() {
    this.processFilteredAssumptions()
  }

  @action
  async loadAssumptionValues(entityId: number) {
    try {
      this.categoryValues = await data(AssumptionApi.getEntityAssumptions(entityId))

      const allFields: IFormField[] = []
      this.categories = this.categoryValues
        .filter((c) => c.entityAssumptions.length > 0)
        .map((c) => {
          const fields = c.entityAssumptions.map((a) => {
            const field = this.createFormField(a.assumption!, a)
            field.meta.showActions = this.showActions
            field.meta.mode = this.getFormFieldMode(a)
            field.meta.labelButton = {
              info: true,
              document: !!a.linkedDocumentId,
            }

            allFields.push(field)

            return field
          })
          return {
            id: c.id,
            name: c.label,
            description: c.description,
            maxNumberOfColumns: c.maxNumberOfColumns,
            fields,
            hidden: false,
            open: false,
          }
        })

      this.setAssumptionFields(allFields)
    } catch (e: any) {
      runInAction(() => {
        this.errorMessage = e
        this.error = true
      })
      console.error('Error loading assumptions.', e)
    }
  }

  getFormFieldMode(assumption: AssumptionValue) {
    if (this.entityDetail?.locked) {
      return IFormFieldMode.ReadOnly
    }
    return assumption.editable ? IFormFieldMode.ClickToEdit : IFormFieldMode.ReadOnly
  }

  @action
  processFilteredAssumptions() {
    let hasAnyMatch = false
    const query = this.searchTerm

    this.assumptionFields.forEach((field) => {
      let included = true

      if (this.filteredAssumptions !== undefined) {
        // don't filter unless items present
        if (this.filteredAssumptions.length) {
          // -1 is the admin mode so all are included unless filtered by search term
          this.filteredAssumptions!.includes(-1)
            ? (included = true)
            : (included = this.filteredAssumptions!.includes(field.id!))
        }
      }
      const label = field.label

      // filter by term
      if (query && label.toLowerCase().indexOf(query.toLowerCase()) === -1) {
        included = false
      }

      if (!included) {
        field.state.filtered = true
      } else {
        field.state.filtered = false
        if (!field.state.hidden) {
          hasAnyMatch = true
        }
      }
    })

    this.categories.forEach((category) => {
      let hide = true
      category.fields.forEach((field) => {
        // category.fields holds separate observable copies; the conditional-hidden
        // and filter state is only maintained on assumptionFields (the single source
        // of truth), so resolve to that before deciding whether to hide the category.
        const sourceField = this.assumptionFieldMap[field.id!] ?? field
        if (!sourceField.state.hidden && !sourceField.state.filtered) {
          hide = false
        }
      })
      category.hidden = hide
      // if there's a search term expand the category if it is visible
      if (query) {
        category.open = true
      }
    })
    this.noAssumptions = !hasAnyMatch
  }

  @action
  expandFirstCategoryCollapseOthers() {
    this.setAllCategoriesState(false)
    // Ensure first category is expanded.
    const firstVisibleCategory = this.categories.find((c) => !c.hidden)
    if (firstVisibleCategory) {
      this.setCategoryState(firstVisibleCategory, true)
    }
  }

  @action
  setDialogState(state: boolean, button?: string, field?: IFormField) {
    this.dialogOpen = state
    if (button) {
      this.dialogType = button
    }
    if (field) {
      this.dialogField = field
    }
  }

  @action
  setCategoryState(category: ICategory, open: boolean) {
    category.open = open
    const foundIndex = this.categories.findIndex((x) => x.id === category.id)
    if (foundIndex >= 0) {
      this.categories[foundIndex] = category
    }
    if (open) {
      this.categoriesAllOpen = open
    } else {
      this.checkForAllClosed()
    }
  }

  @action
  setAllCategoriesState(open: boolean) {
    this.categoriesAllOpen = open
    this.categories.forEach((category) => (category.open = open))
  }

  @action
  checkForAllClosed() {
    let allClosed = true
    this.categories.forEach((c) => {
      if (c.open) {
        allClosed = false
      }
    })
    if (allClosed) {
      this.setAllCategoriesState(false)
    }
  }
}
